import React, { forwardRef, useImperativeHandle, useState } from 'react';
import classNames from 'classnames';
import { Button } from '../Input/Button/Button';
import styles from './styles.module.scss';

interface TabChild {
  key: string;
  name: string;
  render: () => React.ReactNode;
}

interface Props {
  className?: string;
  tabClassName?: string;
  children: TabChild[];
}

export interface TabsRef {
  getActiveChild: () => TabChild;
  setActiveChildByKey: (name: string) => void;
}

export const Tabs = forwardRef<TabsRef, Props>(
  ({ className, tabClassName, children }, ref) => {
    const [activeChildIndex, setActiveChildIndex] = useState(0);

    // expose functions to get and set the active child
    useImperativeHandle(ref, () => ({
      getActiveChild() {
        return children[activeChildIndex];
      },
      setActiveChildByKey(childKey) {
        const childIndex = children.findIndex((c) => c.key === childKey);
        if (childIndex !== -1) {
          setActiveChildIndex(childIndex);
        }
      },
    }));

    return (
      <div className={classNames(styles.container, className)}>
        <div className={styles.tabBar}>
          <div className={styles.tabContainer}>
            {children.map((c, i) => (
              <Button
                key={c.key}
                className={classNames(
                  styles.tab,
                  tabClassName,
                  i === activeChildIndex && styles.active,
                )}
                label={c.name}
                variant="minimal"
                onClick={() => setActiveChildIndex(i)}
              />
            ))}
          </div>
        </div>
        {children.map((child, index) => (
          <div
            key={child.key}
            // render all the children to allow form validation on tabs that aren't visible
            style={{ display: index === activeChildIndex ? undefined : 'none' }}
          >
            {child.render()}
          </div>
        ))}
      </div>
    );
  },
);
